import { Link, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { getArticleBySlug, articles } from '@/mocks/articles';
import Footer from '@/pages/home/components/Footer';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center px-6">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-background-100 flex items-center justify-center">
            <i className="ri-file-warning-line text-2xl text-foreground-400"></i>
          </div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">Article Not Found</h1>
          <p className="text-sm text-foreground-500 mb-6">The article you are looking for does not exist or has been moved.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line"></i>
            </div>
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <ArticleSchema article={article} />
      <main>
        <ArticleNav />
        <ArticleHero article={article} />
        <ArticleContent article={article} />
        <RelatedArticles currentSlug={article.slug} />
        <ArticleCTA />
        <Footer />
      </main>
    </>
  );
}

function ArticleSchema({ article }: { article: typeof articles[0] }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': article.title,
    'description': article.metaDescription,
    'image': article.featuredImage,
    'datePublished': article.datePublished,
    'dateModified': article.datePublished,
    'author': {
      '@type': 'Organization',
      'name': 'Sugargoo Sheet',
    },
    'publisher': {
      '@type': 'Organization',
      'name': 'Sugargoo Sheet',
      'logo': {
        '@type': 'ImageObject',
        'url': 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/1fc56da0b352904925ac780066826afe.jpeg',
      },
    },
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': `https://sugargoosheet.it.com/article/${article.slug}`,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

function ArticleNav() {
  return (
    <nav className="fixed top-0 left-0 w-full z-50 bg-background-50 shadow-sm">
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 group cursor-pointer">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-file-list-3-line text-xl text-primary-500"></i>
            </div>
            <span className="font-heading text-lg font-semibold text-foreground-950 whitespace-nowrap">
              Sugargoo Sheet
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/article/sugargoo-spreadsheet-beginner-tutorial"
              className="hidden sm:flex items-center gap-2 text-sm font-medium text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-book-open-line"></i>
              </div>
              Shopping Guide
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line"></i>
              </div>
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}

function ArticleHero({ article }: { article: typeof articles[0] }) {
  return (
    <section className="pt-28 pb-12 md:pt-32 md:pb-16 bg-background-100">
      <div className="max-w-3xl mx-auto px-6">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-6">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={getCategoryIcon(article.category)}></i>
          </div>
          {article.category}
        </div>
        <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-6">
          {article.title}
        </h1>
        <p className="text-base text-foreground-600 mb-6 leading-relaxed max-w-2xl">
          {article.excerpt}
        </p>
        <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-calendar-line"></i>
            </div>
            Updated {article.datePublished}
          </div>
          <span className="text-foreground-300">·</span>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-time-line"></i>
            </div>
            {article.readTime}
          </div>
          <span className="text-foreground-300">·</span>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-eye-line"></i>
            </div>
            Sugargoo Spreadsheet
          </div>
        </div>
      </div>
    </section>
  );
}

function ArticleContent({ article }: { article: typeof articles[0] }) {
  const { content } = article;

  return (
    <article className="py-12 md:py-16 bg-background-50">
      <div className="max-w-3xl mx-auto px-6">
        <div className="prose-custom">
          <div className="bg-background-100 rounded-xl p-6 mb-10 border border-background-200/60">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-lightbulb-line text-lg text-primary-500"></i>
              </div>
              <div>
                <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-2">Article Summary</p>
                <p className="text-foreground-700 leading-relaxed text-sm">
                  {content.introduction}
                </p>
              </div>
            </div>
          </div>

          {content.sections.map((section, idx) => (
            <div key={idx} className="mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                {section.heading}
              </h2>
              {section.paragraphs.map((para, pIdx) => (
                <p key={pIdx} className="text-foreground-700 leading-relaxed mb-4 text-sm md:text-base">
                  {para}
                </p>
              ))}
            </div>
          ))}

          <div className="bg-accent-50 border border-accent-200/60 rounded-xl p-6 mb-10">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-check-double-line text-lg text-accent-600"></i>
              </div>
              <div>
                <p className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-2">Key Takeaway</p>
                <p className="text-foreground-700 leading-relaxed text-sm">
                  {content.conclusion}
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-background-200 pt-8 mt-12">
            <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">Related Resources</h3>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-background-100 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-home-line"></i>
                </div>
                Sugargoo Spreadsheet Home
              </Link>
              <Link
                to="/article/what-is-sugargoo-spreadsheet"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-background-100 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-article-line"></i>
                </div>
                What Is Sugargoo Spreadsheet
              </Link>
              <Link
                to="/article/sugargoo-spreadsheet-beginner-tutorial"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-background-100 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-book-open-line"></i>
                </div>
                Beginner Tutorial
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-background-100 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-external-link-line"></i>
                </div>
                Visit Main Website
              </a>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

function RelatedArticles({ currentSlug }: { currentSlug: string }) {
  const related = articles.filter((a) => a.slug !== currentSlug).slice(0, 3);

  return (
    <section className="py-14 bg-background-100">
      <div className="max-w-5xl mx-auto px-6">
        <h3 className="font-heading text-xl font-bold text-foreground-950 mb-8 text-center">
          More Sugargoo Spreadsheet Articles
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {related.map((article) => (
            <Link
              key={article.slug}
              to={`/article/${article.slug}`}
              className="group bg-background-50 rounded-xl overflow-hidden transition-all duration-300 hover:ring-2 hover:ring-primary-200 cursor-pointer"
            >
              <div className="aspect-[16/9] w-full overflow-hidden">
                <img
                  src={article.featuredImage}
                  alt={article.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-4">
                <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                  {article.category}
                </span>
                <h4 className="font-heading text-sm font-bold text-foreground-950 mt-2 group-hover:text-primary-600 transition-colors duration-300 line-clamp-2">
                  {article.title}
                </h4>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function ArticleCTA() {
  return (
    <section className="py-14 bg-primary-500">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">
          Ready to Explore the Sugargoo Spreadsheet?
        </h2>
        <p className="text-background-50/85 text-sm mb-8">
          Put your knowledge into action — browse thousands of curated product finds and start shopping smarter today.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg bg-background-50 text-primary-600 font-bold text-sm hover:bg-background-100 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            Sugargoo Spreadsheet
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-up-line"></i>
            </div>
          </a>
          <Link
            to="/"
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-lg border-2 border-background-50/40 text-background-50 font-bold text-sm hover:bg-background-50/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            Back to Home
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-home-line"></i>
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}

function getCategoryIcon(category: string): string {
  const icons: Record<string, string> = {
    'Beginner Guides': 'ri-book-open-line',
    'Reviews': 'ri-star-line',
    'Shipping': 'ri-ship-line',
    'Coupons & Discounts': 'ri-ticket-2-line',
    'Warehouse & QC': 'ri-archive-line',
    'Comparisons': 'ri-scales-3-line',
    'Product Finds': 'ri-shopping-bag-line',
    'Resources': 'ri-link-m',
  };
  return icons[category] || 'ri-article-line';
}